import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { addToDiscord } from "../utils/discord";
import Logo from "../components/Logo";
import "./LandingPage.css";
import SiteFooter from "../components/SiteFooter";
import NotificationBar from "../components/NotificationBar";

const API_BASE = import.meta.env.VITE_API_URL || "";

export default function LandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { trackEvent } = useApp();
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [startingCheckout, setStartingCheckout] = useState(false);

  useEffect(() => {
    // Check if user is logged in
    fetch(`${API_BASE}/api/user`, { credentials: "include" })
      .then(r => r.ok ? r.json() : null)
      .then(u => setIsLoggedIn(!!u))
      .catch(() => setIsLoggedIn(false))
      .finally(() => setCheckingAuth(false));
  }, []);

  function handleLogin() {
    trackEvent("login_click", { location: "landing_page" });
    const next = encodeURIComponent("/dashboard");
    window.location = `${API_BASE}/auth/discord?next=${next}`;
  }

  async function handleUpgradeClick(plan) {
    if (checkingAuth || startingCheckout) return;
    trackEvent("landing_upgrade_click", { plan, logged_in: isLoggedIn });

    if (!isLoggedIn) {
      // Redirect to login with return path to settings with auto-checkout
      const returnPath = encodeURIComponent(`/dashboard/settings?startCheckout=${plan}`);
      window.location = `${API_BASE}/auth/discord?next=${returnPath}`;
      return;
    }

    // User is logged in, start checkout directly
    setStartingCheckout(true);
    try {
      const res = await fetch(`${API_BASE}/api/billing/checkout`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.url) {
        throw new Error(data?.error || "Failed to start checkout");
      }
      window.location = data.url;
    } catch (e) {
      alert(e.message || "Failed to start checkout. Please try again.");
      setStartingCheckout(false);
    }
  }

  function scrollToFeatures() {
    document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
  }

  function scrollToPricing() {
    document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" });
  }

  useEffect(() => {
    if (location.hash === "#features") {
      scrollToFeatures();
    } else if (location.hash === "#pricing") {
      scrollToPricing();
    }
  }, [location.hash]);

  return (
    <div className="landing-page">
      <NotificationBar trackEvent={trackEvent} />
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-content">
          <div className="landing-logo">
            <Logo size={32} />
            <span className="landing-logo-text">okeybot</span>
          </div>
          <div className="landing-nav-links">
            <button className="landing-nav-link" onClick={scrollToFeatures}>
              Features
            </button>
            <button className="landing-nav-link" onClick={scrollToPricing}>
              Pricing
            </button>
            {!checkingAuth && !isLoggedIn && (
              <button className="landing-nav-link" onClick={() => navigate("/dashboard")}>
                Dashboard
              </button>
            )}
            {!checkingAuth && (
              isLoggedIn ? (
                <button className="btn btn-primary" onClick={() => navigate("/dashboard")}>
                  Go to Dashboard
                </button>
              ) : (
                <button className="btn btn-primary" onClick={handleLogin}>
                  Login
                </button>
              )
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <div className="landing-hero-content">
          <div className="landing-hero-badge">
            <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
            </svg>
            <span>Your AI, Your Rules, Your Keys</span>
          </div>

          <h1 className="landing-hero-title">
            Bring Your Own AI
            <br />
            <span className="landing-hero-gradient">To Discord</span>
          </h1>

          <p className="landing-hero-description">
            Chat with Claude, GPT-5, Gemini, and more in Discord. Use your own API keys for full control and 0% markup, or try with built-in models first before adding any keys.
          </p>

          <div className="landing-hero-buttons">
            <button className="btn btn-primary btn-hero" onClick={addToDiscord} data-umami-event="add_to_discord_landing">
              <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
              </svg>
              Add to Discord
            </button>
            <button className="btn btn-secondary btn-hero" onClick={() => navigate("/dashboard")}>
              View Dashboard
              <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
              </svg>
            </button>
          </div>

          <div className="landing-hero-stats">
            <div className="landing-hero-stat">
              <div className="landing-hero-stat-value">7</div>
              <div className="landing-hero-stat-label">AI Providers</div>
            </div>
            <div className="landing-hero-stat">
              <div className="landing-hero-stat-value">0%</div>
              <div className="landing-hero-stat-label">Markup Fees</div>
            </div>
            <div className="landing-hero-stat">
              <div className="landing-hero-stat-value">60s</div>
              <div className="landing-hero-stat-label">Setup Time</div>
            </div>
          </div>
        </div>

        {/* Discord-Style Chat Preview */}
        <div className="landing-hero-visual">
          <div className="discord-chat-preview">
            <div className="discord-chat-header">
              <div className="discord-channel-info">
                <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M5.88657 21C5.57547 21 5.3399 20.7189 5.39427 20.4126L6.00001 17H2.59511C2.28449 17 2.04905 16.7198 2.10259 16.4138L2.27759 15.4138C2.31946 15.1746 2.52722 15 2.77011 15H6.35001L7.41001 9H4.00511C3.69449 9 3.45905 8.71977 3.51259 8.41381L3.68759 7.41381C3.72946 7.17456 3.93722 7 4.18011 7H7.76001L8.39677 3.41262C8.43914 3.17391 8.64664 3 8.88907 3H9.87344C10.1845 3 10.4201 3.28107 10.3657 3.58738L9.76001 7H15.76L16.3968 3.41262C16.4391 3.17391 16.6466 3 16.8891 3H17.8734C18.1845 3 18.4201 3.28107 18.3657 3.58738L17.76 7H21.1649C21.4755 7 21.711 7.28023 21.6574 7.58619L21.4824 8.58619C21.4406 8.82544 21.2328 9 20.9899 9H17.41L16.35 15H19.7549C20.0655 15 20.301 15.2802 20.2474 15.5862L20.0724 16.5862C20.0306 16.8254 19.8228 17 19.5799 17H16L15.3632 20.5874C15.3209 20.8261 15.1134 21 14.8709 21H13.8866C13.5755 21 13.3399 20.7189 13.3943 20.4126L14 17H8.00001L7.36325 20.5874C7.32088 20.8261 7.11337 21 6.87094 21H5.88657ZM9.41045 9L8.35045 15H14.3504L15.4104 9H9.41045Z" />
                </svg>
                <span className="discord-channel-name">ai-chat</span>
                <svg width={16} height={16} fill="currentColor" viewBox="0 0 24 24" style={{ margin: '0 4px' }}>
                  <path d="M9 6l6 6-6 6-1.41-1.41L12.17 12 7.59 7.41 9 6z" />
                </svg>
                <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24" style={{ marginRight: '8px' }}>
                  <path d="M5.43 21.87c-2.44-.84-4.21-3.1-4.21-5.77V5.5C1.22 4.12 2.34 3 3.72 3h16.56c1.38 0 2.5 1.12 2.5 2.5v10.6c0 1.38-1.12 2.5-2.5 2.5H8.63l-3.2 3.27z" />
                </svg>
                <span className="discord-thread-name">Quantum Computing</span>
              </div>
            </div>
            <div className="discord-chat-messages">
              <div className="discord-message">
                <img
                  src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Ccircle cx='50' cy='50' r='50' fill='%235865F2'/%3E%3Ctext x='50' y='50' text-anchor='middle' dominant-baseline='central' fill='white' font-size='50' font-family='Arial, sans-serif'%3EY%3C/text%3E%3C/svg%3E"
                  alt="User"
                  className="discord-avatar"
                />
                <div className="discord-message-content">
                  <div className="discord-message-header">
                    <span className="discord-username">You</span>
                    <span className="discord-timestamp">Today at 2:24 PM</span>
                  </div>
                  <div className="discord-message-text">
                    Explain quantum computing in simple terms
                  </div>
                </div>
              </div>

              <div className="discord-message">
                <div className="discord-avatar discord-bot-avatar">
                  <Logo size={40} />
                </div>
                <div className="discord-message-content">
                  <div className="discord-message-header">
                    <span className="discord-username">okeybot</span>
                    <span className="discord-bot-badge">APP</span>
                    <span className="discord-timestamp">Today at 2:24 PM</span>
                  </div>
                  <div className="discord-message-text discord-typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Providers Section */}
      <section className="landing-providers">
        <div className="landing-section-content">
          <div className="landing-section-header">
            <span className="landing-section-badge">Supported Providers</span>
            <h2 className="landing-section-title">Works With All Major AI Providers</h2>
            <p className="landing-section-description">
              Connect your API keys from any provider. Switch between models seamlessly. All in one place.
            </p>
          </div>

          <div className="landing-providers-grid">
            <div className="landing-provider-card">
              <svg className="landing-provider-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
              </svg>
              <h3>OpenAI</h3>
              <p>GPT‑5.6 Sol & more GPT models</p>
            </div>

            <div className="landing-provider-card">
              <svg className="landing-provider-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z" />
              </svg>
              <h3>Anthropic</h3>
              <p>Fable 5, Opus 5, Sonnet 5 and more</p>
            </div>

            <div className="landing-provider-card">
              <svg className="landing-provider-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" />
              </svg>
              <h3>Google</h3>
              <p>Gemini 3.1 Pro, Flash, Imagen 4, Veo 3.1</p>
            </div>

            <div className="landing-provider-card">
              <svg className="landing-provider-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
              <h3>xAI</h3>
              <p>Grok 4.20, Imagine Video, and Image Pro</p>
            </div>

            <div className="landing-provider-card">
              <svg className="landing-provider-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12.036 2c-3.853-.035-7 3-7.036 6.781-.035 3.782 3.055 6.872 6.908 6.907h2.42v-2.566h-2.292c-2.407.028-4.38-1.866-4.408-4.23-.029-2.362 1.901-4.298 4.308-4.326h.1c2.407 0 4.358 1.915 4.365 4.278v6.305c0 2.342-1.944 4.25-4.323 4.279a4.375 4.375 0 01-3.033-1.252l-1.851 1.818A7 7 0 0012.029 22h.092c3.803-.056 6.858-3.083 6.879-6.816v-6.5C18.907 4.963 15.817 2 12.036 2z" />
              </svg>
              <h3>Groq</h3>
              <p>GPT‑OSS, Kimi K2, Qwen3, Llama 4</p>
            </div>

            <div className="landing-provider-card">
              <svg className="landing-provider-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19.785 0v7.272H22.5V17.62h-2.935V24l-7.037-6.194v6.145h-1.091v-6.152L4.392 24v-6.465H1.5V7.188h2.884V0l7.053 6.494V.19h1.09v6.49L19.786 0zm-7.257 9.044v7.319l5.946 5.234V14.44l-5.946-5.397zm-1.099-.08l-5.946 5.398v7.235l5.946-5.234V8.965zm8.136 7.58h1.844V8.349H13.46l6.105 5.54v2.655zm-8.982-8.28H2.59v8.195h1.8v-2.576l6.192-5.62zM5.475 2.476v4.71h5.115l-5.115-4.71zm13.219 0l-5.115 4.71h5.115v-4.71z" />
              </svg>
              <h3>Perplexity</h3>
              <p>Sonar models with web search</p>
            </div>
            <div className="landing-provider-card">
              <svg className="landing-provider-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16.804 1.957l7.22 4.105v.087L16.73 10.21l.017-2.117-.821-.03c-1.059-.028-1.611.002-2.268.11-1.064.175-2.038.577-3.147 1.352L8.345 11.03c-.284.195-.495.336-.68.455l-.515.322-.397.234.385.23.53.338c.476.314 1.17.796 2.701 1.866 1.11.775 2.083 1.177 3.147 1.352l.3.045c.694.091 1.375.094 2.825.033l.022-2.159 7.22 4.105v.087L16.589 22l.014-1.862-.635.022c-1.386.042-2.137.002-3.138-.162-1.694-.28-3.26-.926-4.881-2.059l-2.158-1.5a21.997 21.997 0 00-.755-.498l-.467-.28a55.927 55.927 0 00-.76-.43C2.908 14.73.563 14.116 0 14.116V9.888l.14.004c.564-.007 2.91-.622 3.809-1.124l1.016-.58.438-.274c.428-.28 1.072-.726 2.686-1.853 1.621-1.133 3.186-1.78 4.881-2.059 1.152-.19 1.974-.213 3.814-.138l.02-1.907z" />
              </svg>
              <h3>OpenRouter</h3>
              <p>Router for dozens of different models</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="landing-features">
        <div className="landing-section-content">
          <div className="landing-section-header">
            <span className="landing-section-badge">Features</span>
            <h2 className="landing-section-title">Everything You Need, Nothing You Don't</h2>
            <p className="landing-section-description">
              Built for power users who want full control over their AI experience
            </p>
          </div>

          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg width={24} height={24} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.65 10C11.83 7.67 9.61 6 7 6c-3.31 0-6 2.69-6 6s2.69 6 6 6c2.61 0 4.83-1.67 5.65-4H17v4h4v-4h2v-4H12.65zM7 14c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2z" />
                </svg>
              </div>
              <h3>Your Keys, Your Control</h3>
              <p>Use your own API keys with 0% markup. Or start with built-in models with free credits, then add keys when you are ready.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg width={24} height={24} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                </svg>
              </div>
              <h3>Multi-Provider Support</h3>
              <p>Connect multiple providers and switch between them instantly. Compare responses, find the best model for each task.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg width={24} height={24} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
                </svg>
              </div>
              <h3>Real-Time Usage Tracking</h3>
              <p>Track every token, every request, every dollar spent. Transparent cost breakdowns with no surprises.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg width={24} height={24} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                </svg>
              </div>
              <h3>Threads, DMs & Server Mentions</h3>
              <p>Chat using your own account in DMs and Threads, or assign a model to act as a dedicated, custom assistant for your entire server community.</p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg width={24} height={24} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z" />
                </svg>
              </div>
              <h3>Privacy First</h3>
              <p>
                Your API keys are encrypted at rest. Your conversations stay between you and your provider. We don't store your messages.
              </p>
            </div>

            <div className="landing-feature-card">
              <div className="landing-feature-icon">
                <svg width={24} height={24} fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
              </div>
              <h3>Model Management & Custom Presets</h3>
              <p>Create custom model presets, set defaults, and configure system prompts. Fine-tune your AI experience to perfection.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="landing-pricing">
        <div className="landing-section-content">
          <div className="landing-section-header">
            <span className="landing-section-badge">Pricing</span>
            <h2 className="landing-section-title">Simple, Transparent Pricing</h2>
            <p className="landing-section-description">
              Start free, upgrade when you need more. No hidden fees, cancel anytime.
            </p>
          </div>

          <div className="pricing-toggle">
            <button
              className={`pricing-toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={`pricing-toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly
              <span className="pricing-save-badge">Save 25%</span>
            </button>
          </div>

          <div className="pricing-cards">
            {/* Free Tier */}
            <div className="pricing-card">
              <div className="pricing-card-header">
                <h3>Free</h3>
                <div className="pricing-price">
                  <span className="pricing-currency">$</span>
                  <span className="pricing-amount">0</span>
                  <span className="pricing-period">/month</span>
                </div>
                <p className="pricing-description">
                  Great for light daily use with built-in models or your own API keys.
                </p>
              </div>
              <ul className="pricing-features">
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span><strong>20 messages/day</strong></span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>3000 one-time credits to try built-in models</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>All supported providers</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>Works with your own API keys</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>0% markup on your own API usage</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>Custom model presets</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>Usage tracking</span>
                </li>
              </ul>
              <button
                className="btn btn-secondary btn-pricing"
                onClick={addToDiscord}
                data-umami-event="add_to_discord_pricing_free"
              >
                Get Started Free
              </button>
            </div>

            {/* Premium Tier */}
            <div className="pricing-card pricing-card-featured">
              <div className="pricing-featured-badge">Most Popular</div>
              <div className="pricing-card-header">
                <h3>Premium</h3>
                <div className="pricing-price">
                  <span className="pricing-currency">$</span>
                  <span className="pricing-amount">
                    {billingCycle === 'monthly' ? '3.99' : '2.99'}
                  </span>
                  <span className="pricing-period">/month</span>
                </div>
                {billingCycle === 'yearly' && (
                  <p className="pricing-description">Billed as $35.88/year</p>
                )}
                {billingCycle === 'monthly' && (
                  <p className="pricing-description">Billed monthly</p>
                )}
              </div>
              <ul className="pricing-features">
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span><strong>Unlimited messages</strong></span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span><strong>Code/text attachments support</strong></span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span><strong>
                    15000 monthly credits for built‑in models
                  </strong></span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>All supported providers</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>Works with your own API keys</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>0% markup on your own API usage</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>Custom model presets</span>
                </li>
                <li>
                  <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
                  </svg>
                  <span>Usage tracking</span>
                </li>
              </ul>
              <button
                className="btn btn-primary btn-pricing"
                onClick={() => handleUpgradeClick(billingCycle === 'monthly' ? 'premium_monthly' : 'premium_yearly')}
                disabled={startingCheckout}
              >
                {startingCheckout ? "Starting Checkout..." : "Upgrade to Premium"}
              </button>
            </div>
          </div>

          <div className="pricing-note">
            <p>
              <strong>Note:</strong> You can try OkeyBot with built-in models before adding any API keys. Both plans let you connect your own keys, with the same Free vs Premium message limits.
              <br />
              Our fees cover the Discord bot, usage tracking, and any Okey Credits we include. When you use your own keys, provider costs (OpenAI, Anthropic, etc.) are billed separately by them.
              <br />
              Free plan one-time credits may change.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="landing-how">
        <div className="landing-section-content">
          <div className="landing-section-header">
            <span className="landing-section-badge">How It Works</span>
            <h2 className="landing-section-title">Get Started in 3 Simple Steps</h2>
            <p className="landing-section-description">
              From signup to your first AI chat in under 2 minutes
            </p>
          </div>

          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step-number">1</div>
              <div className="landing-step-content">
                <h3>Add Bot to Discord</h3>
                <p>Click "Add to Discord" and select your server. The bot will be added instantly with all required permissions.</p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">2</div>
              <div className="landing-step-content">
                <h3>Connect Your API Keys</h3>
                <p>OkeyBot works out of the box with built-in models. When you want more control, log in to the dashboard and add keys from OpenAI, Anthropic, Google, or any supported provider. Your keys are encrypted, secure, and tied to your own account.</p>
              </div>
            </div>

            <div className="landing-step">
              <div className="landing-step-number">3</div>
              <div className="landing-step-content">
                <h3>Start Chatting</h3>
                <p>Use /chat in any Discord channel to create a thread. Switch models anytime with /model. Free plan includes 20 messages per day across built-in models and your own keys.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="landing-cta">
        <div className="landing-cta-content">
          <h2 className="landing-cta-title">Ready to Take Control of Your AI?</h2>
          <p className="landing-cta-description">
            Join users who've switched to OkeyBot for transparent, powerful, and private AI in Discord
          </p>
          <button className="btn btn-primary btn-hero" onClick={addToDiscord} data-umami-event="add_to_discord_now_landing_bottom">
            <svg width={20} height={20} fill="currentColor" viewBox="0 0 24 24">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z" />
            </svg>
            Add to Discord Now
          </button>
          <p className="landing-cta-note">20 free messages daily • Works with your own API keys • No credit card required</p>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}
